/*
 * Shared Layers results controller.
 *
 * Powers the search results page (mode="search") and collection pages
 * (mode="collection") from one <layers-results> element: product grid, Size /
 * Fit / Color facets, sort orders, pagination or infinite scroll, and URL
 * state so results are shareable and back/forward works.
 */

import {
  colorFamilySwatch,
  escapeHtml,
  facetLabel,
  formatMoney,
  getSDK,
  handleize,
  loadFilterModule,
  mediaResponseOptions,
  pinSearchId,
  recallSearchId,
  rememberSearchId,
  SEARCH_ID_PARAM,
  slimProducts,
  sortFacetEntries,
  swatchBackground,
  themeConfig,
} from './layers.js';
import { BANNER_MOBILE_QUERY, placeBanners, sizeBanners } from './layers-banners.js';
import { cardImageSizes, renderProductCard, revealCards } from './layers-card.js';

const SORT_LABELS = {
  relevance: ['relevance', 'relevancy'],
  bestSelling: ['best selling', 'best-selling', 'bestselling', 'best sellers'],
  newest: ['newest', 'newest to oldest', 'new arrivals', 'date new to old'],
  priceLowHigh: ['price low to high', 'price low-high', 'price: low to high', 'price ascending'],
  priceHighLow: ['price high to low', 'price high-low', 'price: high to low', 'price descending'],
  aToZ: ['a-z', 'a to z', 'alphabetically a-z', 'title a-z'],
  zToA: ['z-a', 'z to a', 'alphabetically z-a', 'title z-a'],
};

const SEARCH_SORT_ORDER = ['relevance', 'bestSelling', 'newest', 'priceLowHigh', 'priceHighLow', 'aToZ', 'zToA'];
const COLLECTION_SORT_ORDER = ['bestSelling', 'priceLowHigh', 'priceHighLow', 'newest', 'aToZ', 'zToA'];

const SORT_DISPLAY = {
  relevance: 'Relevance',
  bestSelling: 'Best selling',
  newest: 'Newest',
  priceLowHigh: 'Price low–high',
  priceHighLow: 'Price high–low',
  aToZ: 'A–Z',
  zToA: 'Z–A',
};

/*
 * How each Layers storefront facet renders. Facet order follows the facet
 * metaobjects and value order the Layers response (both set in Layers), so
 * this only maps a code to its control. Unknown facets render as
 * plain checkbox lists.
 */
const FACET_ROLES = {
  'options.size': 'size',
  'options.waist': 'size',
  'options.inseam': 'size',
  'options.color': 'color',
  'variants.price': 'price',
  'metafields.reviews.rating.value': 'rating',
};

const PRICE_CODE = 'variants.price';
/* Shopify's standard product rating (written by Okendo), as Layers' numeric leaf. */
const RATING_CODE = 'metafields.reviews.rating.value';
const RATING_THRESHOLDS = [4, 3, 2, 1];
const STAR_ICON =
  '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false"><path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9z" fill="currentColor"/></svg>';
const TILE_TAG_CODE = 'tags';

function facetRole(definition) {
  return FACET_ROLES[definition.code] || 'list';
}

function normalizeSortLabel(value) {
  return String(value || '').toLowerCase().replace(/[^a-z]/g, '');
}

function sortKeyFor(sortOrder) {
  const label = normalizeSortLabel(sortOrder.nickname || sortOrder.code);
  for (const [key, aliases] of Object.entries(SORT_LABELS)) {
    if (aliases.some((alias) => label === normalizeSortLabel(alias))) return key;
  }
  return null;
}

// The current path with `params`, keeping the hash. No params means no `?`, so
// an unfiltered first load leaves the URL as the shopper typed it.
function urlWithParams(params) {
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
}

export class LayersResults extends HTMLElement {
  connectedCallback() {
    if (this.connected) return;
    this.connected = true;

    this.mode = this.dataset.mode || 'collection';
    this.collectionHandle = this.dataset.collectionHandle;
    this.sectionId = this.dataset.sectionId || 'layers';
    this.facetDefinitions = this.readFacetDefinitions();
    this.showPromotionalPrice = this.dataset.showPromotionalPrice === 'true';
    this.grid = this.querySelector('[data-layers-grid]');
    const portalSelector = 'facets-drawer, .drawer, dialog, x-popover';
    const isMovedHost = (host) => Boolean(host?.isConnected && !this.contains(host) && host.closest(portalSelector));
    const facetHosts = Array.from(this.querySelectorAll('[data-layers-facets]'));
    // An open dialog can still be in <body> when this same collection reconnects.
    // Prefer freshly rendered controls, retaining moved hosts for other contexts.
    const movedFacetHosts = (this.facetHosts || []).filter((host) => isMovedHost(host) &&
      !facetHosts.some((current) => host.dataset.context && current.dataset.context === host.dataset.context));
    this.facetHosts = Array.from(new Set([...facetHosts, ...movedFacetHosts]));
    this.sortHost = this.querySelector('[data-layers-sort]') || (isMovedHost(this.sortHost) ? this.sortHost : null);
    const sectionCountHosts = document
      .getElementById(`shopify-section-${this.sectionId}`)
      ?.querySelectorAll('[data-layers-count]');
    this.countHosts = Array.from(
      new Set([...this.querySelectorAll('[data-layers-count]'), ...(sectionCountHosts || [])]),
    );
    this.paginationHost = this.querySelector('[data-layers-pagination]');
    this.loadMoreButton = this.querySelector('[data-layers-load-more]');
    this.sentinel = this.querySelector('[data-layers-sentinel]');
    this.endState = this.querySelector('[data-layers-end]');
    this.emptyState = this.querySelector('[data-layers-empty]');
    this.errorState = this.querySelector('[data-layers-error]');
    this.loading = false;
    this.facetsData = null;
    // Kept across reconnects so a response requested before a disconnect can't
    // match a request made after it.
    this.requestSequence ??= 0;

    if (!this.grid) return;

    // Every listener below is bound to this signal; disconnectedCallback aborts
    // it so a reconnect doesn't stack a second set of handlers.
    this.lifecycle = new AbortController();
    const { signal } = this.lifecycle;

    window.addEventListener('popstate', () => this.run(true), { signal });

    // The theme moves drawers and (below 1000px) the sort popover to <body>
    // when they open, so their events no longer bubble through this element.
    // Listen on them too; the WeakSet keeps an event that reaches both from
    // running twice.
    const roots = new Set([this]);
    [...this.facetHosts, this.sortHost].forEach((host) => {
      const drawer = host?.closest(portalSelector);
      if (drawer) roots.add(drawer);
    });
    const handled = new WeakSet();
    const listen = (type, handler) =>
      roots.forEach((root) =>
        root.addEventListener(
          type,
          (event) => {
            if (handled.has(event)) return;
            handled.add(event);
            handler(event);
          },
          { signal },
        ),
      );

    listen('change', (event) => {
      const input = event.target;
      if (input === this.sortHost && input.tagName === 'SELECT') {
        this.setSort(input.value);
        return;
      }
      if (input?.dataset?.layersRating) {
        this.setRating(input.checked ? Number(input.dataset.layersRating) : null);
        return;
      }
      // Releasing a price thumb applies the range.
      if (input?.dataset?.layersPriceThumb) {
        this.applyPriceFrom(input.closest('[data-layers-price-range]'));
        return;
      }
      if (!(input instanceof HTMLInputElement) || !input.dataset.layersFilter) return;
      this.toggleFilter(input.dataset.layersFilter, input.value, input.checked);
    });

    // Dragging a price thumb mirrors into its number field (thumbs can't cross).
    listen('input', (event) => {
      const thumb = event.target;
      const bound = thumb?.dataset?.layersPriceThumb;
      if (!bound) return;
      const container = thumb.closest('[data-layers-price-range]');
      const other = container.querySelector(`[data-layers-price-thumb="${bound === 'min' ? 'max' : 'min'}"]`);
      if (bound === 'min' && Number(thumb.value) > Number(other.value)) thumb.value = other.value;
      if (bound === 'max' && Number(thumb.value) < Number(other.value)) thumb.value = other.value;
      container.querySelector(`[data-layers-price="${bound}"]`).value = thumb.value;
      const track = thumb.parentElement;
      const span = Number(container.dataset.max) - Number(container.dataset.min) || 1;
      const pct = (value) => `${((Number(value) - Number(container.dataset.min)) / span) * 100}%`;
      track.style.setProperty('--from', pct(container.querySelector('[data-layers-price-thumb="min"]').value));
      track.style.setProperty('--to', pct(container.querySelector('[data-layers-price-thumb="max"]').value));
    });

    listen('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target?.dataset?.layersPrice) return;
      event.preventDefault();
      this.applyPriceFrom(event.target.closest('[data-layers-price-range]'));
    });

    listen('click', (event) => {
      const priceApply = event.target.closest('[data-layers-price-apply]');
      if (priceApply) {
        event.preventDefault();
        this.applyPriceFrom(priceApply.closest('[data-layers-price-range]'));
        return;
      }

      const sortOption = event.target.closest('[data-layers-sort-option]');
      if (sortOption) {
        event.preventDefault();
        const code = sortOption.dataset.layersSortOption || '';
        this.sortHost.setAttribute('aria-activedescendant', sortOption.id);
        // Prestige binds initial options once; SDK options arrive afterward.
        sortOption.dispatchEvent(new CustomEvent('listbox:select', { bubbles: true, detail: { value: code } }));
        this.setSort(code);
        return;
      }

      if (event.target.closest('[data-layers-back-to-top]')) {
        event.preventDefault();
        // Native fragment navigation fires popstate and would reload results.
        const toolbar = this.querySelector('[data-layers-toolbar]');
        const target = toolbar || this.grid;
        target?.focus({ preventScroll: true });
        // A stuck toolbar is already visible, so scroll its non-sticky parent.
        (toolbar ? this : target)?.scrollIntoView({ block: 'start' });
        return;
      }

      if (event.target.closest('[data-layers-clear-filters]')) {
        event.preventDefault();
        if (this.endState?.contains(event.target)) {
          this.grid?.focus({ preventScroll: true });
          this.grid?.scrollIntoView({ block: 'start' });
        }
        this.clearFilters();
        return;
      }

      if (event.target.closest('[data-layers-load-more]')) {
        event.preventDefault();
        if (!this.loading) this.appendPage(this.state().page + 1);
        return;
      }

      const pageLink = event.target.closest('[data-layers-page]');
      if (pageLink) {
        event.preventDefault();
        this.setPage(Number(pageLink.dataset.layersPage));
      }
    });

    // C2: shop-by-fit tiles (.box[data-tag]) toggle the Fit facet value.
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    section?.querySelectorAll('.box[data-tag]').forEach((box) => {
      box.addEventListener('click', () => this.toggleFitTag(box.dataset.tag), { signal });
    });

    // Banners have separate web/mobile layouts; re-place them when the device
    // flips, and keep 2×2 spans within the columns the grid has.
    this.bannerQuery = window.matchMedia(BANNER_MOBILE_QUERY);
    this.bannerQuery.addEventListener?.('change', () => this.renderBanners(), { signal });
    if (typeof ResizeObserver !== 'undefined') {
      this.bannerResize = new ResizeObserver(() => sizeBanners(this.grid));
      this.bannerResize.observe(this.grid);
    }

    this.initSentinel();
    this.renderFacetSkeleton();
    // Sort orders don't depend on the results request; start them now so the
    // sort popover isn't a second round-trip after the grid renders.
    this.resolveSortOrders();
    this.run(true);
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    // A late response from a prior connection must not update this element.
    this.requestSequence = (this.requestSequence || 0) + 1;
    this.sentinelObserver?.disconnect();
    this.bannerResize?.disconnect();
    this.connected = false;
  }

  readFacetDefinitions() {
    try {
      const parsed = JSON.parse(this.dataset.facets || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /*
   * Page size for a fresh result sequence. Appended pages reuse the size the
   * sequence started with (resultPageSize) so a breakpoint change between
   * Load more clicks can't skip or repeat products.
   */
  get pageSize() {
    const mobile = window.matchMedia('(max-width: 699px)').matches;
    return Number(mobile ? this.dataset.pageSizeMobile : this.dataset.pageSizeDesktop) || 24;
  }

  get perRowDesktop() {
    return Number(this.dataset.perRowDesktop) || 4;
  }

  /* Card `sizes`; also sent to Layers so its preload hints match the grid. */
  get cardSizes() {
    // The search page's desktop filter sidebar: 13.75rem column + 2.5rem gap.
    const sidebar = this.querySelector('.search-results__sidebar');
    return cardImageSizes({
      stacked: this.dataset.stacked !== 'false',
      productsPerRowDesktop: this.perRowDesktop,
      productsPerRowMobile: this.perRowMobile,
      reservedDesktopPx: sidebar ? 260 : 0,
    });
  }

  get perRowMobile() {
    return Number(this.dataset.perRowMobile) || 2;
  }

  state() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    const allowedCodes = new Set((this.facetDefinitions || []).map((definition) => definition.code));
    const price = {};
    let rating = null;
    for (const [key, value] of params.entries()) {
      if (!key.startsWith('filter.')) continue;
      const code = key.slice('filter.'.length);
      // Rating is a minimum: filter.metafields.reviews.rating.value.gte (1–5).
      if (code === `${RATING_CODE}.gte`) {
        const stars = Number(value);
        if (value !== '' && Number.isFinite(stars) && stars > 0) rating = stars;
        continue;
      }
      if (code === RATING_CODE) continue;
      // Price is a range: filter.variants.price.gte / .lte (whole currency units).
      if (code === `${PRICE_CODE}.gte` || code === `${PRICE_CODE}.lte`) {
        const amount = Number(value);
        if (value !== '' && Number.isFinite(amount) && amount >= 0) price[code.endsWith('gte') ? 'min' : 'max'] = amount;
        continue;
      }
      if (code === PRICE_CODE) continue;
      if (code !== TILE_TAG_CODE && allowedCodes.size > 0 && !allowedCodes.has(code)) continue;
      (filters[code] ||= []).push(value);
    }

    return {
      query: params.get('q') || '',
      sort: params.get('sort') || '',
      page: Math.max(1, Number(params.get('page')) || 1),
      filters,
      price,
      rating,
    };
  }

  async filterGroupFor(state) {
    const entries = Object.entries(state.filters).filter(([, values]) => values.length > 0);
    const price = state.price || {};
    const hasPrice = price.min != null || price.max != null;
    if (entries.length === 0 && !hasPrice && state.rating == null) return undefined;

    const { filter, and, inValues, gte, lte } = await loadFilterModule();
    // and() is variadic; values within a facet are OR'd by inValues.
    const expressions = entries.map(([code, values]) => inValues(code, values));
    if (price.min != null) expressions.push(gte(PRICE_CODE, price.min));
    if (price.max != null) expressions.push(lte(PRICE_CODE, price.max));
    if (state.rating != null) expressions.push(gte(RATING_CODE, state.rating));
    return filter(and(...expressions));
  }

  updateUrl(state, { replace = false } = {}) {
    const params = new URLSearchParams(window.location.search);

    Array.from(params.keys()).forEach((key) => {
      if (key.startsWith('filter.') || key === 'sort' || key === 'page') params.delete(key);
    });

    if (state.sort) params.set('sort', state.sort);
    if (state.page > 1) params.set('page', String(state.page));
    for (const [code, values] of Object.entries(state.filters)) {
      values.forEach((value) => params.append(`filter.${code}`, value));
    }
    if (state.price?.min != null) params.set(`filter.${PRICE_CODE}.gte`, String(state.price.min));
    if (state.price?.max != null) params.set(`filter.${PRICE_CODE}.lte`, String(state.price.max));
    if (state.rating != null) params.set(`filter.${RATING_CODE}.gte`, String(state.rating));

    const url = urlWithParams(params);
    replace ? history.replaceState({}, '', url) : history.pushState({}, '', url);
  }

  toggleFilter(code, value, checked) {
    const state = this.state();
    const values = new Set(state.filters[code] || []);
    checked ? values.add(value) : values.delete(value);
    state.filters[code] = Array.from(values);
    if (state.filters[code].length === 0) delete state.filters[code];
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  setSort(code) {
    const state = this.state();
    state.sort = code;
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  setPage(page) {
    const state = this.state();
    state.page = page;
    this.updateUrl(state);
    this.run();
  }

  /*
   * Shop-by-fit tiles (.box[data-tag], e.g. `brian-fit`, `ross-stacked`)
   * filter on the product tag itself: the brand's fit_code metafield is
   * missing or wrong on many products (most Brian jeans have none), while
   * the tile tags are complete. Tags aren't a sidebar facet, so this filter
   * only comes from the tiles.
   */
  toggleFitTag(tag) {
    if (!tag) return;
    const state = this.state();
    const values = new Set(state.filters[TILE_TAG_CODE] || []);
    values.has(tag) ? values.delete(tag) : values.add(tag);
    state.filters[TILE_TAG_CODE] = Array.from(values);
    if (state.filters[TILE_TAG_CODE].length === 0) delete state.filters[TILE_TAG_CODE];
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  fitTagActive(tag) {
    return (this.state().filters[TILE_TAG_CODE] || []).includes(tag);
  }

  syncFitTiles() {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    section?.querySelectorAll('.box[data-tag]').forEach((box) => {
      box.classList.toggle('selected', this.fitTagActive(box.dataset.tag));
    });
  }

  /* The URL advances to `page` only once that page has rendered. */
  appendPage(page) {
    if (this.loadMoreButton) this.loadMoreButton.disabled = true;
    this.run(false, { page });
  }

  /**
   * One search controller per query, so pages, sorts and filters all execute
   * against a single search_id: the one in the URL, else the one the quick
   * search prepared, else the SDK's first prepare. The adopted ID is written
   * back to the URL (without a history entry) for the pixel and shared links.
   */
  searchControllerFor(sdk, query) {
    if (this.searchController?.query === query) return this.searchController.controller;

    const urlSearchId = new URLSearchParams(window.location.search).get(SEARCH_ID_PARAM);
    const pin = pinSearchId(urlSearchId || recallSearchId(query), (searchId) => rememberSearchId(query, searchId));
    const transformBody = slimProducts((body) => {
      const pinned = pin(body);
      if (pinned?.search_id) this.syncSearchIdParam(pinned.search_id);
      return pinned;
    });

    const controller = sdk.search({ query, transformBody });
    this.searchController = { query, controller };
    return controller;
  }

  syncSearchIdParam(searchId) {
    const params = new URLSearchParams(window.location.search);
    if (params.get(SEARCH_ID_PARAM) === searchId) return;
    params.set(SEARCH_ID_PARAM, searchId);
    history.replaceState(history.state, '', urlWithParams(params));
  }

  async run(replace = false, appendTo = null) {
    const append = Boolean(appendTo);
    const state = this.state();
    if (append) state.page = appendTo.page;
    const sequence = ++this.requestSequence;
    this.loading = true;
    if (this.endState) this.endState.hidden = true;
    if (this.sentinel) this.sentinel.hidden = false;
    if (!append && this.mode === 'collection') {
      this.atEnd = false;
      if (this.sentinel) this.sentinel.dataset.next = 'layers';
      // The prior total cannot describe the replacement results while pending.
      this.grid?.removeAttribute('data-collection-total-products');
    }
    if (!append || !this.resultPageSize) this.resultPageSize = this.pageSize;
    const limit = this.resultPageSize;

    let sdk, filterGroup;
    try {
      [sdk, filterGroup] = await Promise.all([getSDK(), this.filterGroupFor(state)]);
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.showError(error, { append });
      return;
    }

    if (sequence !== this.requestSequence) return;

    const facetCodes = this.facetDefinitions.map((facet) => facet.code);
    const sort = state.sort || undefined;
    // Preload hints only for the first screen; appended pages land below it.
    const responseOptions = mediaResponseOptions({ sizes: this.cardSizes, enabled: !append });

    try {
      let result;
      if (this.mode === 'search') {
        result = await this.searchControllerFor(sdk, state.query).get({
          sort,
          responseOptions,
          pagination: { page: state.page, limit },
          facets: facetCodes,
          retrieveFacetCount: true,
          includeFacetRanges: true,
          filterGroup,
        });
      } else {
        result = await sdk.collection({ collectionHandle: this.collectionHandle, transformBody: slimProducts() }).get({
          sort,
          responseOptions,
          pagination: { page: state.page, limit },
          facets: facetCodes,
          retrieveFacetCount: true,
          includeFacetRanges: true,
          filterGroup,
        });
      }

      if (sequence !== this.requestSequence) return;
      this.loading = false;

      const { data, error } = result;
      if (error) {
        this.showError(error, { append });
        return;
      }

      const redirect = data?._meta?.redirect?.url;
      if (redirect && !append && this.isSafeRedirect(redirect)) {
        // Replace, so Back skips the results URL instead of redirecting again.
        window.location.replace(redirect);
        return;
      }

      this.facetsData = data;
      if (replace || append) this.updateUrl(state, { replace: true });
      this.renderProducts(data, append, state);
      this.renderFacets(state);
      this.renderSorts(state);
      this.renderCount(data, state);
      this.renderPagination(data, state);
      this.syncEndState(data);
      this.syncFitTiles();
      this.dispatchEvent(new CustomEvent('layers:state-changed', { bubbles: true }));
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      this.showError(error, { append });
    }
  }

  renderProducts(data, append, state) {
    const ctx = {
      stacked: this.dataset.stacked !== 'false',
      reveal: this.dataset.reveal === 'true',
      hideProductInformation: this.dataset.hideProductInformation === 'true',
      showPromotionalPrice: this.showPromotionalPrice,
      sectionId: this.sectionId,
      productsPerRowDesktop: this.perRowDesktop,
      productsPerRowMobile: this.perRowMobile,
      sizes: this.cardSizes,
    };

    if (!append) this.grid.replaceChildren();

    const products = data?.products || data?.results || [];
    const firstRow = append ? 0 : Math.max(this.perRowDesktop, this.perRowMobile);
    const cards = products.map((product, index) => renderProductCard(product, { ...ctx, priority: index < firstRow }));
    this.grid.append(...cards);
    revealCards(cards);

    const total = data?.totalResults ?? products.length;
    if (this.mode === 'collection') {
      this.grid.dataset.collectionTotalProducts = String(total);
    }

    // Banners come with the first page; appended pages only add products.
    if (!append) {
      this.banners = products.length > 0 && Array.isArray(data?.banners) ? data.banners : [];
      this.renderBanners();
    }

    const isEmpty = !append && products.length === 0;
    if (this.emptyState) {
      this.emptyState.hidden = !isEmpty;
    }
    this.grid.hidden = isEmpty;
    // Applied filters stay reachable (and removable) when they match nothing.
    const price = state?.price || {};
    const filtered =
      Object.keys(state?.filters || {}).length > 0 || price.min != null || price.max != null || state?.rating != null;
    this.facetHosts.forEach((host) => (host.hidden = isEmpty && !filtered));
    this.querySelectorAll('[data-layers-empty-clear]').forEach((button) => (button.hidden = !filtered));
    if (this.errorState) this.errorState.hidden = true;
  }

  renderBanners() {
    if (!this.grid) return;
    if (!this.bannerHero && (this.banners?.length || 0) > 0) {
      this.bannerHero = document.createElement('div');
      this.bannerHero.className = 'layers-banners-hero';
      this.bannerHero.hidden = true;
      this.grid.before(this.bannerHero);
    }
    placeBanners({
      grid: this.grid,
      heroHost: this.bannerHero,
      banners: this.banners || [],
      mobile: Boolean(this.bannerQuery?.matches),
      sizes: this.cardSizes,
    });
  }

  isSafeRedirect(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  renderFacets(state) {
    const facets = this.facetsData?.facets || {};
    const { settings = {}, strings = {} } = themeConfig();
    const defaultOpen = this.dataset.openFilters === 'true';
    const price = state.price || {};
    const priceActive = price.min != null || price.max != null;
    const ratingActive = state.rating != null;
    const activeCount =
      Object.values(state.filters).reduce((sum, values) => sum + values.length, 0) + (priceActive ? 1 : 0) + (ratingActive ? 1 : 0);
    const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;

    // The price slider's bounds come from the unfiltered-by-price response, so
    // narrowing the range doesn't shrink the control under the shopper. A page
    // opened with a price filter has no such response yet: start from this
    // one, widened to the applied range, until an unfiltered one replaces it.
    const range = this.facetsData?.facetRanges?.[PRICE_CODE];
    if (range && !priceActive) {
      this.priceBounds = range;
    } else if (priceActive && !this.priceBounds) {
      const bounds = [range?.min, range?.max, price.min, price.max].filter((value) => Number.isFinite(value));
      if (bounds.length > 0) this.priceBounds = { min: Math.min(...bounds), max: Math.max(...bounds) };
    }

    // Layers returns ratings as per-value counts ("4.8": 13). Like price, the
    // options come from the response before a rating filter narrows it.
    const ratingCounts = facets[RATING_CODE];
    if (ratingCounts && !ratingActive) this.ratingCounts = ratingCounts;

    this.facetHosts.forEach((host, hostIndex) => {
      // Keep each accordion's open/closed state across re-renders.
      const openCodes = new Map(
        Array.from(host.querySelectorAll('details[data-facet-code]'), (details) => [details.dataset.facetCode, details.open]),
      );

      const groups = this.facetDefinitions
        .map((definition) => {
          const role = facetRole(definition);
          const active = state.filters[definition.code] || [];
          const isOpen = (fallback) =>
            openCodes.has(definition.code) ? openCodes.get(definition.code) : defaultOpen || fallback;
          const idPrefix = `layers-facet-${this.sectionId}-${hostIndex}-${handleize(definition.code)}`;

          if (role === 'price') {
            return this.priceFacetMarkup(definition, { idPrefix, open: isOpen(priceActive), price, strings });
          }

          if (role === 'rating') {
            return this.ratingFacetMarkup(definition, { idPrefix, open: isOpen(ratingActive), rating: state.rating, strings });
          }

          const values = facets[definition.code];
          const entries = Object.entries(values || {});
          // A filter with a single option can't narrow anything; keep it only
          // while one of its values is applied, so it can be removed.
          if (entries.length < 2 && active.length === 0) return '';

          const isColor = role === 'color';
          const isSize = role === 'size';
          // Value order comes from Layers. Sizes are the exception: integer-like
          // keys ("2", "30") are hoisted when the response is parsed, so they get
          // a fixed wearable order.
          const sorted = isSize ? sortFacetEntries(entries, { isSize: true }) : entries;

          const items = sorted
            .map(([value, count], valueIndex) => {
              const id = `${idPrefix}-${valueIndex}`;
              const checked = active.includes(value) ? ' checked' : '';
              const input = `<input id="${id}" type="checkbox" data-layers-filter="${escapeHtml(definition.code)}" value="${escapeHtml(value)}"${checked}`;

              if (isColor) {
                const family = colorFamilySwatch(value);
                const white = family ? family.light : (strings.whiteLabel || 'white').toLowerCase() === String(value).toLowerCase();
                const classes = ['color-swatch', settings.roundColorSwatches ? 'rounded-full' : '', white ? 'border' : '']
                  .filter(Boolean)
                  .join(' ');
                const background = family ? `--swatch-background: ${family.background}` : swatchBackground(value);
                return `${input} class="sr-only"><label class="${classes}" for="${id}" data-tooltip="${escapeHtml(value)}" style="${escapeHtml(background)}"><span class="sr-only">${escapeHtml(value)} (${count})</span></label>`;
              }

              if (isSize) {
                return `${input} class="sr-only"><label class="layers-size-chip" for="${id}">${escapeHtml(value)}<span class="sr-only"> (${count})</span></label>`;
              }

              return `<div class="checkbox-control">${input} class="dot-checkbox"><label for="${id}">${escapeHtml(value)} (${count})</label></div>`;
            })
            .join('');

          return `<details class="accordion group" is="accordion-disclosure" data-facet-code="${escapeHtml(definition.code)}"${isOpen(active.length > 0) ? ' open aria-expanded="true"' : ' aria-expanded="false"'}>
            <summary>
              <span class="accordion__toggle h6">
                <span>${escapeHtml(facetLabel(definition.name))}</span>
                ${strings.chevronDownIcon || ''}
              </span>
            </summary>
            <div class="accordion__content">
              <div class="${isColor ? 'h-stack wrap gap-2' : isSize ? 'layers-size-grid' : 'v-stack gap-2.5'}">${items}</div>
            </div>
          </details>`;
        })
        .join('');

      const clearAll =
        activeCount > 0
          ? `<button type="button" class="link text-sm" data-layers-clear-filters>${escapeHtml(strings.clearFilters || 'Clear all')}</button>`
          : '';

      host.innerHTML =
        groups || clearAll ? `<div class="facets v-stack gap-4">${clearAll}<div class="accordion-list">${groups}</div></div>` : '';
    });

    if (focusedId) document.getElementById(focusedId)?.focus();
  }

  /*
   * Price: a min/max pair over the Layers facet range (whole dollars), applied
   * with a button or Enter. Two range thumbs sit over the inputs for pointer
   * users; the number fields stay the accessible source of truth.
   */
  priceFacetMarkup(definition, { idPrefix, open, price, strings }) {
    const bounds = this.priceBounds;
    if (!bounds || !(bounds.max > bounds.min)) return '';

    const floor = Math.floor(bounds.min);
    const ceil = Math.ceil(bounds.max);
    const min = price.min ?? floor;
    const max = price.max ?? ceil;
    const currency = (formatMoney(0).match(/^[^\d\s]+/) || ['$'])[0];

    return `<details class="accordion group" is="accordion-disclosure" data-facet-code="${escapeHtml(definition.code)}"${open ? ' open aria-expanded="true"' : ' aria-expanded="false"'}>
      <summary>
        <span class="accordion__toggle h6">
          <span>${escapeHtml(facetLabel(definition.name))}</span>
          ${strings.chevronDownIcon || ''}
        </span>
      </summary>
      <div class="accordion__content">
        <div class="layers-price-range" data-layers-price-range data-min="${floor}" data-max="${ceil}">
          <div class="layers-price-range__track" aria-hidden="true" style="--from: ${((min - floor) / (ceil - floor)) * 100}%; --to: ${((max - floor) / (ceil - floor)) * 100}%">
            <input type="range" class="layers-price-range__thumb" min="${floor}" max="${ceil}" step="1" value="${min}" data-layers-price-thumb="min" tabindex="-1">
            <input type="range" class="layers-price-range__thumb" min="${floor}" max="${ceil}" step="1" value="${max}" data-layers-price-thumb="max" tabindex="-1">
          </div>
          <div class="layers-price-range__inputs">
            <label class="layers-price-range__field" for="${idPrefix}-min">
              <span class="sr-only">${escapeHtml(strings.priceFrom || 'Minimum price')}</span>
              <span aria-hidden="true">${escapeHtml(currency)}</span>
              <input id="${idPrefix}-min" type="number" inputmode="numeric" min="${floor}" max="${ceil}" step="1" value="${min}" data-layers-price="min">
            </label>
            <span class="layers-price-range__dash" aria-hidden="true">–</span>
            <label class="layers-price-range__field" for="${idPrefix}-max">
              <span class="sr-only">${escapeHtml(strings.priceTo || 'Maximum price')}</span>
              <span aria-hidden="true">${escapeHtml(currency)}</span>
              <input id="${idPrefix}-max" type="number" inputmode="numeric" min="${floor}" max="${ceil}" step="1" value="${max}" data-layers-price="max">
            </label>
          </div>
          <button type="button" class="layers-price-range__apply" data-layers-price-apply>${escapeHtml(strings.applyPrice || 'Apply')}</button>
        </div>
      </div>
    </details>`;
  }

  /*
   * Rating: single-select "N stars & up" thresholds with the number of
   * products reaching each, listed while some product does. Checkboxes so the
   * applied one can be unchecked to clear it.
   */
  ratingFacetMarkup(definition, { idPrefix, open, rating, strings }) {
    const values = Object.entries(this.ratingCounts || {}).map(([value, count]) => [Number(value), Number(count) || 0]);
    const reaching = (stars) => values.reduce((sum, [value, count]) => (value >= stars ? sum + count : sum), 0);
    const thresholds = RATING_THRESHOLDS.filter((stars) => stars === rating || reaching(stars) > 0);
    if (thresholds.length === 0) return '';

    const template = strings.ratingAndUp || '{{ count }} & up';
    const items = thresholds
      .map((stars) => {
        const id = `${idPrefix}-${stars}`;
        const label = template.replace('{{ count }}', stars);
        const icons = Array.from({ length: 5 }, (_, index) => `<span class="layers-rating__star${index < stars ? ' is-filled' : ''}">${STAR_ICON}</span>`).join('');
        return `<div class="checkbox-control"><input id="${id}" type="checkbox" class="dot-checkbox" data-layers-rating="${stars}"${stars === rating ? ' checked' : ''}><label for="${id}" class="layers-rating"><span class="layers-rating__stars" aria-hidden="true">${icons}</span><span>${escapeHtml(label)} (${reaching(stars)})</span></label></div>`;
      })
      .join('');

    return `<details class="accordion group" is="accordion-disclosure" data-facet-code="${escapeHtml(definition.code)}"${open ? ' open aria-expanded="true"' : ' aria-expanded="false"'}>
      <summary>
        <span class="accordion__toggle h6">
          <span>${escapeHtml(facetLabel(definition.name))}</span>
          ${strings.chevronDownIcon || ''}
        </span>
      </summary>
      <div class="accordion__content">
        <div class="v-stack gap-2.5">${items}</div>
      </div>
    </details>`;
  }

  setRating(stars) {
    const state = this.state();
    state.rating = Number.isFinite(stars) && stars > 0 ? stars : null;
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  applyPriceFrom(container) {
    const floor = Number(container.dataset.min);
    const ceil = Number(container.dataset.max);
    const read = (bound) => {
      const value = container.querySelector(`[data-layers-price="${bound}"]`)?.value;
      if (value === '' || value == null) return null;
      const amount = Math.round(Number(value));
      return Number.isFinite(amount) ? Math.min(Math.max(amount, floor), ceil) : null;
    };

    // Bounds equal to the full range mean "no limit" on that side.
    let min = read('min');
    let max = read('max');
    if (min === floor) min = null;
    if (max === ceil) max = null;
    this.setPriceRange(min, max);
  }

  setPriceRange(min, max) {
    const state = this.state();
    state.price = {};
    if (min != null) state.price.min = min;
    if (max != null) state.price.max = max;
    if (min != null && max != null && min > max) state.price = { min: max, max: min };
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  clearFilters() {
    const state = this.state();
    state.filters = {};
    state.price = {};
    state.rating = null;
    state.page = 1;
    this.updateUrl(state);
    this.run();
  }

  renderFacetSkeleton() {
    // Placeholder accordions so the drawer/sidebar is never a blank panel
    // while the first Layers response is in flight.
    const skeleton = this.facetDefinitions
      .map(
        (definition) => `<details class="accordion group" aria-busy="true">
          <summary><span class="accordion__toggle h6"><span>${escapeHtml(facetLabel(definition.name))}</span></span></summary>
        </details>`,
      )
      .join('');
    if (!skeleton) return;
    this.facetHosts.forEach((host) => {
      if (host.childElementCount === 0) {
        host.innerHTML = `<div class="facets"><div class="accordion-list">${skeleton}</div></div>`;
      }
    });
  }

  resolveSortOrders() {
    this.sortOrdersPromise ||= this.fetchSortOrders();
    return this.sortOrdersPromise;
  }

  async fetchSortOrders() {
    try {
      const sdk = await getSDK();
      const controller = sdk.sortOrders();
      const { data, error } = await controller.get();
      if (error) throw error;

      const allowed = this.mode === 'search' ? SEARCH_SORT_ORDER : COLLECTION_SORT_ORDER;
      const mapped = [];
      (data?.sortOrders || []).forEach((sortOrder) => {
        const key = sortKeyFor(sortOrder);
        if (key && allowed.includes(key)) {
          mapped.push({ key, code: sortOrder.code, label: SORT_DISPLAY[key] });
        }
      });

      const seen = new Set();
      this.sortOrdersResolved = allowed
        .map((key) => mapped.find((entry) => entry.key === key))
        .filter(Boolean)
        .filter((entry) => (seen.has(entry.code) ? false : (seen.add(entry.code), true)));
    } catch {
      this.sortOrdersResolved = [];
    }

    return this.sortOrdersResolved;
  }

  async renderSorts(state) {
    if (!this.sortHost) return;
    const sequence = this.requestSequence;
    const orders = await this.resolveSortOrders();
    // A newer request (or a disconnect) renders its own sorts.
    if (sequence !== this.requestSequence) return;
    if (orders.length === 0) {
      this.sortHost.hidden = true;
      return;
    }

    this.sortHost.hidden = false;
    const active = state.sort || (this.mode === 'search' ? '' : this.dataset.defaultSort || '');

    this.sortHost.replaceChildren();
    // Search lists Relevance once, as the default (no sort code); drop the
    // API's own relevance order so it isn't listed twice.
    const blank = this.mode === 'search' ? [{ key: 'relevance', code: '', label: SORT_DISPLAY.relevance }] : [];
    const listed = this.mode === 'search' ? orders.filter((order) => order.key !== 'relevance') : orders;

    // The search page uses a native <select>; collections use the popover listbox.
    if (this.sortHost.tagName === 'SELECT') {
      [...blank, ...listed].forEach((order) => {
        const option = new Option(order.label, order.code, false, order.code === active);
        this.sortHost.appendChild(option);
      });
      this.sortHost.value = active;
      return;
    }

    [...blank, ...listed].forEach((order, index) => {
      const option = document.createElement('button');
      option.id = `layers-sort-${this.sectionId}-${index}`;
      option.value = order.code;
      option.type = 'button';
      option.className = 'popover-listbox__option group';
      option.setAttribute('role', 'option');
      option.dataset.layersSortOption = order.code;
      option.setAttribute('aria-selected', String(order.code === active));
      option.innerHTML = `<span class="reversed-link">${escapeHtml(order.label)}</span>`;
      this.sortHost.appendChild(option);
    });
    const selected = this.sortHost.querySelector('[aria-selected="true"]');
    if (selected) this.sortHost.setAttribute('aria-activedescendant', selected.id);
    else this.sortHost.removeAttribute('aria-activedescendant');
  }

  renderCount(data, state) {
    const total = data?.totalResults ?? 0;
    this.countHosts.forEach((host) => {
      const template = (total === 1 && host.dataset.templateOne) || host.dataset.template || '{{ count }} products';
      host.textContent = template.replace('{{ count }}', total).replace('__COUNT__', total);
    });

    // Shopify's <title> counts its own search results; show the Layers count.
    const titleTemplate = (total === 1 && this.dataset.titleTemplateOne) || this.dataset.titleTemplate;
    if (titleTemplate) document.title = titleTemplate.replace('__COUNT__', total);
    void state;
  }

  renderPagination(data, state) {
    if (!this.paginationHost) return;
    this.paginationHost.replaceChildren();

    const totalPages = data?.totalPages ?? 0;
    if (totalPages <= 1) return;

    const list = document.createElement('ol');
    list.className = 'pagination__list';
    for (let page = 1; page <= totalPages; page += 1) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `?page=${page}`;
      link.dataset.layersPage = String(page);
      link.className = page === state.page ? 'pagination__link is-active' : 'pagination__link';
      link.textContent = String(page);
      item.appendChild(link);
      list.appendChild(item);
    }
    this.paginationHost.appendChild(list);
  }

  initSentinel() {
    if (!this.sentinel || this.mode !== 'collection') return;

    this.sentinel.dataset.next = 'layers';
    this.sentinel.innerHTML = `<div class="loadingBox">
      <span class="loadingScroll" style="left:0;animation-delay:0.6s">&nbsp;</span>
      <span class="loadingScroll" style="left:29px;animation-delay:0.75s">&nbsp;</span>
      <span class="loadingScroll" style="left:58px;animation-delay:0.9s">&nbsp;</span>
      <span class="loadingScroll" style="left:88px;animation-delay:1.05s">&nbsp;</span>
      <span class="loadingScroll" style="left:117px;animation-delay:1.2s">&nbsp;</span>
      <span class="loadingScroll" style="left:146px;animation-delay:1.35s">&nbsp;</span>
      <span class="loadingScroll" style="left:175px;animation-delay:1.5s">&nbsp;</span>
      <span class="loadingScroll" style="left:205px;animation-delay:1.64s">&nbsp;</span>
    </div>`;

    this.nextPage = this.state().page;
    this.sentinelObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || this.loading || this.atEnd) return;
        this.appendPage(this.nextPage + 1);
      });
    });
    this.sentinelObserver.observe(this.sentinel);
  }

  syncEndState(data) {
    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = this.state().page >= (data?.totalPages ?? 0);
      this.loadMoreButton.disabled = false;
    }

    if (!this.sentinel || this.mode !== 'collection') return;

    const totalPages = data?.totalPages ?? 0;
    this.nextPage = this.state().page;
    this.atEnd = this.nextPage >= totalPages;

    const hasProducts = Boolean(this.grid?.querySelector('product-card'));
    const state = this.state();
    const filtered = Object.keys(state.filters || {}).length > 0 ||
      state.price?.min != null || state.price?.max != null || state.rating != null;
    if (this.endState) {
      this.endState.hidden = !this.atEnd || !hasProducts;
      this.endState.querySelectorAll('[data-layers-end-default]').forEach((node) => (node.hidden = filtered));
      this.endState.querySelectorAll('[data-layers-end-filtered]').forEach((node) => (node.hidden = !filtered));
    }
    this.sentinel.hidden = this.atEnd;
    this.sentinel.dataset.next = this.atEnd ? '' : 'layers';

    if (this.atEnd) {
      this.sentinelObserver?.unobserve(this.sentinel);
    } else {
      this.sentinelObserver?.observe(this.sentinel);
    }
  }

  showError(error, { append = false } = {}) {
    this.loading = false;
    if (this.loadMoreButton) this.loadMoreButton.disabled = false;
    console.error('Layers request failed:', error);
    // A failed extra page keeps the loaded cards; the URL still names the last
    // rendered page, so Load more retries the same one.
    if (append) return;
    if (this.endState) this.endState.hidden = true;
    if (this.sentinel) this.sentinel.hidden = true;
    if (this.errorState) this.errorState.hidden = false;
    if (this.emptyState) this.emptyState.hidden = false;
    this.grid?.replaceChildren();
  }
}

if (typeof window !== 'undefined' && !window.customElements.get('layers-results')) {
  window.customElements.define('layers-results', LayersResults);
}
